import { SlidersHorizontal, ChevronUp, ChevronDown, RotateCcw } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { ALL_PROPS, defaultTaskCfg } from "@/lib/taskProperties";

const DENSITIES = [["compact", "Compact"], ["comfortable", "Comfortable"], ["detailed", "Detailed"]];
const PROP_LABEL = Object.fromEntries(ALL_PROPS.map((p) => [p.key, p]));

// Personal "Display" control: density + which optional properties are visible
// and their order. Persists through useTaskViewPrefs (existing preferences).
export function TaskViewControls({ cfg, update, ctx = "all" }) {
  const hidden = new Set(cfg.hidden || []);
  const order = cfg.order && cfg.order.length ? cfg.order : ALL_PROPS.map((p) => p.key);

  const toggle = (key) => {
    const nextHidden = new Set(hidden);
    nextHidden.has(key) ? nextHidden.delete(key) : nextHidden.add(key);
    update({ hidden: [...nextHidden] });
  };
  const move = (key, dir) => {
    const i = order.indexOf(key), j = i + dir;
    if (j < 0 || j >= order.length) return;
    const next = [...order]; [next[i], next[j]] = [next[j], next[i]];
    update({ order: next });
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid="task-display-btn"
          className="inline-flex items-center gap-1.5 rounded-lg border border-border/80 bg-card px-3 py-1.5 text-xs font-medium text-muted-foreground shadow-soft transition-colors hover:bg-muted/60 hover:text-foreground">
          <SlidersHorizontal className="h-3.5 w-3.5" /> Display
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-0" data-testid="task-display-panel">
        <div className="border-b border-border/60 p-3">
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Density</p>
          <div className="grid grid-cols-3 gap-1 rounded-lg bg-muted p-1">
            {DENSITIES.map(([k, label]) => (
              <button key={k} onClick={() => update({ density: k })} data-testid={`task-density-${k}`}
                className={cn("rounded-md py-1.5 text-xs font-medium transition-colors", cfg.density === k ? "bg-background text-foreground shadow-sm" : "text-muted-foreground")}>
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className="max-h-72 overflow-y-auto p-3">
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Properties</p>
          <div className="space-y-0.5">
            {order.map((key) => {
              const def = PROP_LABEL[key]; if (!def) return null;
              const Icon = def.icon; const on = !hidden.has(key);
              return (
                <div key={key} className="flex items-center gap-2 rounded-md px-1.5 py-1 hover:bg-muted/50" data-testid={`prop-row-${key}`}>
                  <button onClick={() => toggle(key)} data-testid={`prop-toggle-${key}`}
                    className={cn("flex flex-1 items-center gap-2 text-left text-xs", on ? "text-foreground" : "text-muted-foreground/60")}>
                    <span className={cn("flex h-4 w-4 items-center justify-center rounded border", on ? "border-accent bg-accent text-accent-foreground" : "border-border")}>
                      {on && <span className="h-1.5 w-1.5 rounded-full bg-current" />}
                    </span>
                    <Icon className="h-3.5 w-3.5" /> {def.label}
                  </button>
                  <button onClick={() => move(key, -1)} data-testid={`prop-up-${key}`} className="rounded p-0.5 text-muted-foreground/60 hover:bg-muted hover:text-foreground"><ChevronUp className="h-3.5 w-3.5" /></button>
                  <button onClick={() => move(key, 1)} data-testid={`prop-down-${key}`} className="rounded p-0.5 text-muted-foreground/60 hover:bg-muted hover:text-foreground"><ChevronDown className="h-3.5 w-3.5" /></button>
                </div>
              );
            })}
          </div>
        </div>
        <div className="flex justify-end border-t border-border/60 p-2">
          <Button variant="ghost" size="sm" onClick={() => update(defaultTaskCfg(ctx))} data-testid="task-display-reset">
            <RotateCcw className="mr-1 h-3.5 w-3.5" /> Reset to default
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
